import { Button } from "@fluentui/react-components";
import { ArrowClockwise20Regular } from "@fluentui/react-icons";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { checkThereIsACallInProgress, refreshAccount } from "@/repository/queryFns/account";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshStatus } from "@/repository/models";
import { useMessage } from "@/common/messages";
import { RefreshAgentButtonProps } from "./types";
import { refreshOtherAccount } from "@/repository/queryFns/supervisor";
import { interactionsQueryKey } from "@/repository/queryFns/interactions";
import { captureEvent } from "@/common/helpers/sentryHelper";
import { useSentryAppContext } from "@/hooks/sentry";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { useCallback, useContext } from "react";
import { GlobalDialogContext } from "../dialog/context";

export const RefreshAgentButton = ({
	agentToRefreshId,
}: RefreshAgentButtonProps) => {
	const intl = useIntl();
	const classes = useStyle();
	const queryClient = useQueryClient();
	const { show: showMessage } = useMessage();
	const { getAppContext } = useSentryAppContext();
	const { showDialog, hideDialog } = useContext(GlobalDialogContext);


	const resetAccountMutation = useMutation({
		mutationFn: async (targetAgentId?: string) => {
			const isAnyCallInprogress = await checkThereIsACallInProgress(targetAgentId);

			if (isAnyCallInprogress) {
				showConfirmationDialog(targetAgentId);
			} else {
				await refreshUser(false, targetAgentId)
			}
		}
	});

	const displayResponseMessage = (result: RefreshStatus) => {
		switch (result) {
			case RefreshStatus.RefreshPerformed:
				showMessage("global", {
					text: intl.formatMessage({ id: "Account.RefreshPerformed" }),
					intent: "success",
				});

				if (!agentToRefreshId) {
					queryClient.invalidateQueries({ queryKey: [interactionsQueryKey] });
				}

				break;
			case RefreshStatus.CallInProgress:
				showMessage("global", {
					text: intl.formatMessage({
						id: agentToRefreshId
							? "Account.RefreshFailed.CallInProgressForSupervisor"
							: "Account.RefreshFailed.CallInProgressForAgent",
					}),
					intent: "error",
				});

				break;
			case RefreshStatus.DndStatusIsNotAllowed:
				showMessage("global", {
					text: intl.formatMessage({
						id: agentToRefreshId
							? "Account.RefreshFailed.DndStatusIsNotAllowedForSupervisor"
							: "Account.RefreshFailed.DndStatusIsNotAllowedForAgent",
					}),
					intent: "error",
				});

				break;
			case RefreshStatus.RefreshCouldNotBePerformed:
				showMessage("global", {
					text: intl.formatMessage({
						id: "Account.RefreshFailed.Unknown",
					}),
					intent: "error",
				});

				break;
		}
	}

	const refreshUser = async (deleteOngoingCall: boolean, targetAgentId?: any) => {
		hideDialog();
		let result: RefreshStatus;

		if (targetAgentId) {
			result = await refreshOtherAccount(targetAgentId, deleteOngoingCall);
		} else {
			result = await refreshAccount(deleteOngoingCall);
		}

		displayResponseMessage(result);
	}

	const showConfirmationDialog = useCallback((targetAgentId?: any) => {
		const message = intl.formatMessage(
			{ id: "Account.RefreshedDeleteOngoingCall" }
		);
		const label = intl.formatMessage({ id: "Account.RefreshAgent" });

		showDialog(
			label,
			<div>
				<p>{message}</p>
				<div>
					<Button
						appearance="secondary"
						onClick={() => refreshUser(false, targetAgentId)}>
						{intl.formatMessage({ id: "History.No" })}
					</Button>
					<Button
						style={{ marginLeft: "10px" }}
						appearance="primary"
						onClick={() => refreshUser(true, targetAgentId)}
					>
						{intl.formatMessage({ id: "History.Yes" })}
					</Button>
				</div>
			</div>
		);
	}, [
		showDialog,
		hideDialog,
		intl
	]);

	return (
		<Button
			appearance="transparent"
			className={classes.button}
			icon={<ArrowClockwise20Regular />}
			children={intl.formatMessage({ id: "Account.RefreshAgent" })}
			disabled={resetAccountMutation.isPending}
			onClick={(event: React.MouseEvent<HTMLElement>) => {
				event.stopPropagation();
				captureEvent("Agent refresh action performed", {
					refreshedBySupervisor: !!agentToRefreshId,
					agentToRefreshId: agentToRefreshId || Lockr.get(StorageKeys.UserId),
					...getAppContext(),
				});

				resetAccountMutation.mutate(agentToRefreshId);
			}}
		/>
	);
};

